import {
  DEFAULT_MODEL_ID,
  type Effort,
  MODEL_IDS,
  type ModelId as Model,
  defaultEffortForModel,
  isEffort,
  isValidModelId,
} from '../../../server/src/model-catalog';

export {
  DEFAULT_MODEL_ID,
  MODEL_CATALOG,
  type Effort,
  type ModelId as Model,
  clampEffortForModel,
  effortOptionsForModel,
  effortsForModel,
} from '../../../server/src/model-catalog';
export type PermissionMode = 'bypassPermissions' | 'plan' | 'acceptEdits';

const MODEL_KEY = 'mc.model';
const THEME_KEY = 'mc.theme';
const EFFORT_KEY = 'mc.effort';
const LEGACY_MIGRATION_KEY = 'mc.profile.legacy-migrated';
const DEFAULT_PERMISSION: PermissionMode = 'bypassPermissions';

/**
 * Per-browser user preferences. Kept in localStorage so each profile
 * remembers its choices on this device. Server-side per-user prefs
 * (custom system prompt, etc.) land in Phase 3.
 */

let activeThemeProfileId: number | null = null;

function profileKey(userId: number, key: string): string {
  return `mc.profile.${userId}.${key}`;
}

/** Assign the unscoped preferences to this authenticated profile once, then remove them. */
export function activateProfilePreferences(userId: number): void {
  if (!localStorage.getItem(LEGACY_MIGRATION_KEY)) {
    for (const key of [MODEL_KEY, EFFORT_KEY, THEME_KEY]) {
      const oldValue = localStorage.getItem(key);
      const target = profileKey(userId, key.slice(3));
      if (oldValue !== null && localStorage.getItem(target) === null)
        localStorage.setItem(target, oldValue);
      localStorage.removeItem(key);
    }
    localStorage.setItem(LEGACY_MIGRATION_KEY, String(userId));
  }
  activeThemeProfileId = userId;
  applyTheme();
}

export function deactivateProfilePreferences(): void {
  activeThemeProfileId = null;
  applyTheme('system');
}

/** Type guard for a known model alias — used to validate model ids
 * that arrive as bare strings (e.g. a server `model_fallback` frame's
 * `toModel`) before they're treated as a `Model`. */
export function isModel(v: string): v is Model {
  return isValidModelId(v);
}

export function readModel(userId: number | null): Model {
  const v = userId === null ? null : localStorage.getItem(profileKey(userId, 'model'));
  return isValidModelId(v) ? v : DEFAULT_MODEL_ID;
}

export function writeModel(m: Model, userId: number | null): void {
  if (userId !== null) localStorage.setItem(profileKey(userId, 'model'), m);
  emitPrefsChange();
}

/**
 * Clamp a model to the user's allowlist. `allowed` null = no
 * restriction (return as-is). Otherwise, if the model isn't permitted,
 * fall back to the first allowed model so a stale localStorage choice
 * (e.g. opus for a sonnet-only user) never leaks a forbidden default.
 */
export function clampModelToAllowed(model: Model, allowed: string[] | null): Model {
  if (!allowed || allowed.length === 0) return model;
  if (allowed.includes(model)) return model;
  const first = MODEL_IDS.find((m) => allowed.includes(m));
  return first ?? model;
}

export function readEffort(model: Model, userId: number | null): Effort {
  const v = userId === null ? null : localStorage.getItem(profileKey(userId, 'effort'));
  return isEffort(v) ? v : defaultEffortForModel(model);
}

export function writeEffort(e: Effort, userId: number | null): void {
  if (userId !== null) localStorage.setItem(profileKey(userId, 'effort'), e);
  emitPrefsChange();
}

/* Model/effort live in localStorage but are edited from the header
 * and read by the composer, so writers notify subscribers
 * (useSyncExternalStore in useModelChoice). */
const prefsListeners = new Set<() => void>();
let prefsVersion = 0;

function emitPrefsChange(): void {
  prefsVersion += 1;
  for (const listener of prefsListeners) listener();
}

export function subscribePrefs(listener: () => void): () => void {
  prefsListeners.add(listener);
  return () => prefsListeners.delete(listener);
}

export function getPrefsVersion(): number {
  return prefsVersion;
}

export function readPermission(): PermissionMode {
  // Plan and Accept Edits controls are not exposed; ignore stale preferences.
  return DEFAULT_PERMISSION;
}

export type Theme = 'light' | 'dark' | 'system';

export function readTheme(userId: number | null): Theme {
  const v = userId === null ? null : localStorage.getItem(profileKey(userId, 'theme'));
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
}

export function writeTheme(t: Theme, userId: number | null): void {
  if (userId !== null) localStorage.setItem(profileKey(userId, 'theme'), t);
}

/** Apply (or re-apply) the classes that tokens.css keys off: theme and material. */
export function applyTheme(t: Theme = readTheme(activeThemeProfileId)): void {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const dark = t === 'dark' || (t === 'system' && prefersDark);
  document.documentElement.classList.toggle('dark', dark);
  applyMaterial();
}

/**
 * Surface material. `glass` — «Жидкое стекло»: translucent chrome over a soft
 * colour field; `solid` — opaque surfaces. Everyone can pick either; Apple
 * devices default to glass, where it matches the system look.
 */
export type Material = 'glass' | 'solid';

export function isAppleDevice(
  nav: Pick<Navigator, 'userAgent'> | undefined = globalThis.navigator,
): boolean {
  // iPadOS reports a Mac user agent, so «Macintosh» covers it too.
  return !!nav && /iPhone|iPad|iPod|Macintosh|Mac OS X/.test(nav.userAgent);
}

export function defaultMaterial(nav?: Pick<Navigator, 'userAgent'>): Material {
  return isAppleDevice(nav) ? 'glass' : 'solid';
}

export function readMaterial(userId: number | null = activeThemeProfileId): Material {
  const v = userId === null ? null : localStorage.getItem(profileKey(userId, 'material'));
  return v === 'glass' || v === 'solid' ? v : defaultMaterial();
}

export function writeMaterial(m: Material, userId: number | null): void {
  if (userId !== null) localStorage.setItem(profileKey(userId, 'material'), m);
}

export function applyMaterial(m: Material = readMaterial()): void {
  document.documentElement.classList.toggle('glass', m === 'glass');
  // SVG lensing in backdrop-filter works in Chromium only (navigator.userAgentData);
  // WebKit, including Chrome on iOS, has no userAgentData and skips it.
  const lens =
    m === 'glass' &&
    typeof navigator !== 'undefined' &&
    'userAgentData' in navigator &&
    !('GestureEvent' in globalThis);
  document.documentElement.classList.toggle('lg-lens', lens);
}
